<template>
  <v-card outlined class="card-center">
    <v-container class="px-1 py-1" fluid>
      <ValidationObserver ref="observer" v-slot="{ invalid }">
        <v-card outlined>
          <v-card-title>
            {{ $t('ELE0028') }}
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="4">
                <PartCodeInput v-if="isEpson" v-model="searchCondition.partCd" required @change="onChange" />
                <SupplierPartInput
                  v-else
                  v-model="searchCondition.partCd"
                  :supplierCd="''"
                  isSearchByLoginCompany
                  required
                  @change="onChange"
                />
              </v-col>
              <v-col cols="4">
                <ManufacturerInput v-model="searchCondition.manufacturerCd" />
              </v-col>
              <!-- <v-col cols="2">
                <OqcIqcInput v-model="searchCondition.inspTimingCd" required />
              </v-col> -->
              <v-col cols="2">
                <InspTimingDtlInput
                  v-model="searchCondition.inspTimingDtlCd"
                  :inspTimingCd="inspTimingCdForDtl"
                  :inspClassCd="inspClassCd"
                  required
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="2">
                <TextFieldInputWithDatePicker :label="$t('ELE0266')" v-model="searchCondition.endDateFrom" />
              </v-col>
              <span class="px-4 py-2 mt-3"> {{ $t('ELE0141') }} </span>
              <v-col cols="2">
                <TextFieldInputWithDatePicker :label="$t('ELE0266')" v-model="searchCondition.endDateTo" />
              </v-col>
              <v-col cols="3">
                <SelectMultipleInput
                  :label="$t('ELE0091')"
                  v-model="searchCondition.characCds"
                  :items="characCdList"
                  required
                />
              </v-col>
              <v-col cols="3">
                <SelectSingleInput
                  :label="$t('ELE0270')"
                  v-model="searchCondition.xAxis"
                  item-text="name"
                  item-value="value"
                  :items="xAxisList"
                  required
                />
              </v-col>
              <span class="d-flex flex-row space-between mr-4">
                <BtnSearch class="mt-2" @click="onClickSearch" :disabled="invalid ? true : isRequired" />
              </span>
            </v-row>
          </v-card-text>
        </v-card>
      </ValidationObserver>
    </v-container>
    <v-card outlined v-if="hasFinishedGetData">
      <v-card outlined v-for="(item, index) in items" :key="index">
        <v-card-text>
          <v-row>
            <v-col cols="2">
              <TextFieldRef :label="$t('ELE0091')" :value="item.charac" />
            </v-col>
            <v-col cols="2">
              <TextFieldRef :label="$t('ELE0092')" :value="item.point" />
            </v-col>
            <v-col cols="2">
              <TextFieldRef :label="$t('ELE0157')" :value="item.cav" />
            </v-col>
            <v-col cols="2">
              <TextFieldRef :label="$t('ELE0499')" :value="item.cl" />
            </v-col>
            <v-col cols="2">
              <TextFieldRef :label="$t('ELE0500')" :value="item.ucl" />
            </v-col>
            <v-col cols="2">
              <TextFieldRef :label="$t('ELE0501')" :value="item.lcl" />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <PlotChartCard :measuredValues="item.data" :propsXRange="xRange[index]" />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-pagination class="white" v-model="page[index]" :length="pageMax[index]" @input="pageChange(index)" />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-card>
  </v-card>
</template>

<script>
import { API } from '@/plugins/api'
import moment from 'moment'
import { ValidationObserver } from 'vee-validate'
import { OqcIqcCd, InspClassCd } from '@/enum.js'
import APIUtils from '@/mixin/api-utils.js'
import Message from '@/mixin/message-utils.js'
import Loading from '@/mixin/loading-utils.js'

import SelectSingleInput from '@/components/atoms/base/select/SelectSingleInput'
import SelectMultipleInput from '@/components/atoms/base/select/SelectMultipleInput'
import TextFieldInput from '@/components/atoms/base/text-field/TextFieldInput'
import TextFieldRef from '@/components/atoms/base/text-field/TextFieldRef'

import BtnSearch from '@/components/atoms/button/BtnSearch'
import PartCodeInput from '@/components/atoms/select/PartCodeInput'
import ManufacturerInput from '@/components/atoms/select/ManufacturerInput'
import OqcIqcInput from '@/components/atoms/select/OqcIqcInput'
import TextFieldInputWithDatePicker from '@/components/atoms/base/text-field/TextFieldInputWithDatePicker'
import SupplierPartInput from '@/components/atoms/select/SupplierPartInput'
import PlotChartCard from '@/components/organisms/report/card/PlotChartCard'
import InspTimingDtlInput from '@/components/atoms/select/InspTimingDtlInput'

const ChartXAxis = {
  LOT_AVERAGE: '0',
  EVERY_MEASURED_VALUE: '1',
}
const MAX_DATA_PER_PAGE = 30

export default {
  name: 'MeasValuePlotChartForm',

  mixins: [APIUtils, Message, Loading],

  components: {
    ValidationObserver,
    SelectSingleInput,
    SelectMultipleInput,
    TextFieldInput,
    TextFieldRef,
    BtnSearch,
    PartCodeInput,
    ManufacturerInput,
    OqcIqcInput,
    TextFieldInputWithDatePicker,
    SupplierPartInput,
    PlotChartCard,
    InspTimingDtlInput,
  },

  data() {
    return {
      page: [],
      pageMax: [],
      xRange: [],
      items: [],
      characCdList: [],
      hasFinishedGetData: false,
      defectItems: [],
      inspTimingCdForDtl: this.$store.getters.canOQCOnly ? OqcIqcCd.OQC : '',
      inspClassCd: InspClassCd.DIM,
      searchCondition: {
        partCd: '',
        manufacturerCd: '',
        inspTimingDtlCd: '',
        endDateFrom: moment().subtract(1, 'd').format('YYYY-MM-DD'),
        endDateTo: moment().format('YYYY-MM-DD'),
        characCds: [],
        xAxis: '',
      },
    }
  },

  computed: {
    isRequired: {
      get() {
        let result = true
        if (this.searchCondition.partCd != '' || this.searchCondition.manufacturerCd != '') {
          result = false
        } else {
          result = true
        }
        return result
      },
    },
    isEpson: {
      get() {
        let result = false
        if (this.$store.getters.isEpson) {
          result = true
        }
        return result
      },
    },
    xAxisList: {
      get() {
        // プルダウン選択肢をenumから生成
        return Object.values(ChartXAxis).map((item) => {
          return { name: this.$t(`ChartXAxis.${item}`), value: item }
        })
      },
    },
  },

  methods: {
    pageChange(index) {
      this.xRange[index] = [
        MAX_DATA_PER_PAGE * (this.page[index] - 1),
        Math.min(MAX_DATA_PER_PAGE * this.page[index] - 1, this.items[index]['itemLength'] - 1),
      ]
    },
    async onChange() {
      this.startLoading()
      try {
        this.characCdList = []
        this.searchCondition.characCds = []
        if (!this.searchCondition.partCd) {
          return
        }
        const params = {
          partCd: this.searchCondition.partCd,
        }

        this.characCdList = await API.get('/master/inspCharacDtls/charac', params)
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0271') })))
      } finally {
        this.stopLoading()
      }
    },
    async onClickSearch() {
      this.hasFinishedGetData = false
      this.startLoading()
      try {
        if (this.searchCondition.characCds.length > 5) {
          this.setErrorMessage(this.$t('MSG0088'))
          return
        }
        const params = {
          partCd: this.searchCondition.partCd,
          manufacturerCd: this.searchCondition.manufacturerCd,
          inspTimingDtlCd: this.searchCondition.inspTimingDtlCd,
          characCds: this.searchCondition.characCds,
          endDateFrom: this.searchCondition.endDateFrom,
          endDateTo: this.searchCondition.endDateTo,
          xAxis: this.searchCondition.xAxis,
        }

        this.items = await API.get('/inspDim/measuredPlot', params)
        this.items.forEach((item) => {
          item.data.forEach((e) => {
            e['controlLimit'] = {
              cl: item['cl'],
              ucl: item['ucl'],
              lcl: item['lcl'],
            }
          })
        })
        if (this.items.length) {
          for (let i = 0; i < this.items.length; i++) {
            this.page[i] = 1
            this.calcPage(i)
          }
        }
        this.hasFinishedGetData = true
        setImmediate(() => {
          if (this.items.length) {
            for (let i = 0; i < this.items.length; i++) {
              this.page[i] = 1
            }
          }
        })
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0259') })))
      } finally {
        this.stopLoading()
      }
    },
    calcPage(index) {
      this.xRange[index] = [
        MAX_DATA_PER_PAGE * (this.page[index] - 1),
        Math.min(MAX_DATA_PER_PAGE * this.page[index] - 1, this.items[index]['itemLength'] - 1),
      ]
      this.pageMax[index] = Math.ceil(this.items[index]['itemLength'] / MAX_DATA_PER_PAGE)
    },
  },
}
</script>
